import React, { useState, useRef, useEffect } from 'react';
import useViewport from 'Common/hooks/useViewport';
import { useSpring } from '@react-spring/web';

import { Wrapper, Button, Icon, Dropdown, StyledLabel } from './Dropdown.styled';
import { Chevron } from '@solent-university/solent-icons';

export interface Props {
    id: string;
    button: {
        label: string;
        ariaLabel: string;
    };
    textColor?: {
        label?: React.CSSProperties['color'];
        dropdown?: React.CSSProperties['color'];
    };
    children?: React.ReactNode;
}

const Select: React.FC<Props> = ({ id, button, children, textColor }) => {
    const [isOpen, setIsOpen] = useState(false);
    const { width } = useViewport();
    const [dropdownHeight, setDropdownHeight] = useState(0);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const wrapperRef = useRef<HTMLDivElement>(null);

    const dropdownSpring = useSpring({
        transform: isOpen ? 'scaleY(1)' : 'scaleY(0)',
        opacity: isOpen ? '1' : '0',
        config: { mass: 1, tension: 180, friction: 30 }
    });

    useEffect(() => {
        const mouseHandler = e => {
            if (
                wrapperRef.current instanceof HTMLDivElement &&
                !wrapperRef.current.contains(e.target)
            ) {
                setIsOpen(false);
            }
        };

        document.addEventListener('mousedown', mouseHandler);
        document.addEventListener('keydown', mouseHandler);

        if (dropdownRef.current) {
            const { scrollHeight } = dropdownRef.current;
            setDropdownHeight(scrollHeight);
        }

        return () => {
            document.removeEventListener('mousedown', mouseHandler);
            document.addEventListener('keydown', mouseHandler);
        };
    }, [width, isOpen]);

    return (
        <Wrapper
            ref={wrapperRef}
            style={
                {
                    '--label-text-color': textColor?.label,
                    '--dropdown-color-left': textColor?.dropdown
                } as React.CSSProperties
            }
        >
            <StyledLabel>{button.label}:</StyledLabel>
            <Button
                aria-label={button.ariaLabel}
                onClick={() => setIsOpen(!isOpen)}
                is-active={isOpen}
                aria-expanded={isOpen}
                aria-controls={`dropdown_${id}`.toLowerCase().replace(/\s+/g, '')}
                $isOpen={isOpen}
                type="button"
            >
                <span>{button.label}</span>
                <Icon as={Chevron} />
            </Button>
            <Dropdown
                id={`dropdown_${id}`.toLowerCase().replace(/\s+/g, '')}
                ref={dropdownRef}
                $isOpen={isOpen}
                style={{
                    ...dropdownSpring,
                    maxHeight: isOpen ? dropdownHeight : 0
                }}
                role="listbox"
            >
                {children}
            </Dropdown>
        </Wrapper>
    );
};

export default Select;
